/**
 * A supplier price that is probably a typo, and what each supplier is being given.
 *
 * ---- The failure this catches ----
 *
 * A supplier keys `1.2` where they meant `12`. It wins the allocation outright — cheapest-first
 * ranking never questions its own input — the customer is quoted from it, and the error surfaces
 * when the goods are invoiced at ten times the quote. The comparison screen shows every offer, so
 * the number is on screen; what is missing is anything saying it looks wrong.
 *
 * ---- Why a MEDIAN, and why three offers ----
 *
 * The comparison is against what the other suppliers said, not against a fixed threshold: prices
 * differ wildly by item and a rule like "under 5 AED is suspicious" is meaningless across a
 * catalogue of 18,836 things. The median of the other offers is the market for that line, and an
 * order of magnitude away from it is not competition.
 *
 * THREE offers minimum, and that is not arbitrary. With two, there is no majority to be an
 * outlier from — either could be the typo, and naming one accuses the wrong supplier half the
 * time. Better to say nothing than to point at the honest one.
 *
 * Nothing is discarded or reordered. A flagged offer still ranks exactly where its price puts it;
 * the purchaser decides.
 */

import type { AllocationRow } from './types'

/**
 * How far from the others counts as suspicious.
 *
 * A quarter, and four times, so the reported case (1.2 against 12) is caught with room to spare
 * while ordinary competition — a supplier a third cheaper, which is common and real — is not.
 * Flagging a market as an error trains people to ignore the warning, which costs more than the
 * warning saves.
 */
const LOW = 0.25
const HIGH = 4

/** The minimum offers needed for "the others" to mean anything — see the header. */
const MIN_OFFERS = 3

export interface PriceOutlier {
  item_code: string
  name: string
  enquiry_row_no: string
  supplier: string
  rate: number
  /** What the other suppliers said, for the message. */
  median: number
  direction: 'below' | 'above'
}

/** The middle value — with an even count, the lower of the two middles, so it is always a real
 *  price somebody actually quoted rather than an average of two. */
function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor((sorted.length - 1) / 2)]
}

export function detectPriceOutliers(
  items: readonly {
    item_code: string
    name?: string
    enquiry_row_no?: string
    offers: readonly { supplier: string; unitInBase: number }[]
  }[],
): PriceOutlier[] {
  const out: PriceOutlier[] = []
  for (const item of items) {
    // A zero is a DECLINED reply, not an infinitely cheap offer — the rule `quoteBenchmark.ts`
    // and the workbook reader both apply. Reading it as a price would flag every decline.
    const priced = item.offers.filter((o) => Number.isFinite(o.unitInBase) && o.unitInBase > 0)
    if (priced.length < MIN_OFFERS) continue

    /*
      The median of ALL the offers, not of "the others".

      Excluding the offer under test looks more careful and is worse: with three offers, removing
      the outlier leaves two, and the median of two is one of them — so 1.2 dragged the yardstick
      down and every HONEST price then measured as ten times "the market". All three got flagged.

      A median over the whole set cannot be moved by one value once there are three, which is
      exactly why the minimum is three.
    */
    const mid = median(priced.map((o) => o.unitInBase))
    if (mid <= 0) continue

    for (const offer of priced) {
      const ratio = offer.unitInBase / mid
      if (ratio > LOW && ratio < HIGH) continue
      out.push({
        item_code: item.item_code,
        name: item.name ?? item.item_code,
        enquiry_row_no: item.enquiry_row_no ?? '',
        supplier: offer.supplier,
        rate: offer.unitInBase,
        median: mid,
        direction: ratio <= LOW ? 'below' : 'above',
      })
    }
  }
  return out
}

/**
 * The warning, worded so it prompts a check rather than an undo.
 *
 * It says explicitly that nothing was removed, because the honest reading of "possible error" is
 * otherwise "we have already dealt with it" — and the offer is still ranked and still allocatable.
 */
export function describeOutliers(outliers: readonly PriceOutlier[]): string {
  if (outliers.length === 0) return ''
  const lines = [...new Set(outliers.map((o) => o.item_code))].length
  const first = outliers[0]
  return (
    `Possible pricing error on ${lines} line${lines === 1 ? '' : 's'} — ` +
    `${first.supplier}'s price for ${first.name} is far ${first.direction} the others ` +
    `(likely a supplier typo). Verify before sending; nothing was discarded and everything is still ranked.`
  )
}

export interface SupplierTotal {
  supplier: string
  lines: number
  total: number
}

/**
 * What each supplier is actually being given, biggest first.
 *
 * The screen totalled the whole allocation but never split it by supplier, so "who is getting
 * most of this order" — the question behind every negotiation — could not be read off it.
 */
export function supplierTotals(allocations: readonly AllocationRow[]): SupplierTotal[] {
  const by = new Map<string, SupplierTotal>()
  for (const row of allocations) {
    // A row allocated nothing is not business for that supplier.
    if (row.allocated_qty <= 0) continue
    const current = by.get(row.supplier) ?? { supplier: row.supplier, lines: 0, total: 0 }
    current.lines += 1
    current.total += row.allocated_qty * row.rate
    by.set(row.supplier, current)
  }
  return [...by.values()]
    .map((s) => ({ ...s, total: Math.round(s.total * 100) / 100 }))
    .sort((a, b) => b.total - a.total)
}
